import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Moon, LogOut, Film, BookOpen, Gift, AlertTriangle, Info, Download,
  ArrowUpRight, Headphones, ShieldAlert, GraduationCap,
} from 'lucide-react';
import {
  footageContent, islamicContent, bonusContent,
  perhatianText, caraInstal, heroImage,
} from '../mock';

export default function MemberDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Kamu telah keluar.');
    navigate('/');
  };

  const openLink = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  return (
    <div className="min-h-screen bg-[#f4eede]">
      {/* Header */}
      <header className="sticky top-0 z-40 islamic-pattern border-b border-[#d9a94e]/15">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg gold-gradient flex items-center justify-center">
              <Moon className="w-4 h-4 text-[#0a3b2c]" />
            </div>
            <span className="text-white font-medium">Pahala Digital</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/80">
            <a href="#footage" className="hover:text-[#d9a94e] transition-colors">Footage</a>
            <a href="#islamic" className="hover:text-[#d9a94e] transition-colors">Konten Islamic</a>
            <a href="#bonus" className="hover:text-[#d9a94e] transition-colors">Bonus</a>
          </nav>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-[#d9a94e] text-sm font-semibold leading-tight">{user?.name || 'Member'}</p>
              <p className="text-white/60 text-xs">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-1.5 border border-white/25 text-white text-sm px-3.5 py-2 rounded-lg hover:bg-white/10 transition">
              <LogOut className="w-4 h-4" /> Keluar
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative islamic-pattern overflow-hidden">
        <img src={heroImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <span className="inline-block border border-[#d9a94e]/50 text-[#d9a94e] text-xs font-semibold tracking-[0.2em] px-4 py-1.5 rounded-full mb-6">
            PAKET EKSKLUSIF 7.000+ KONTEN ISLAMIC
          </span>
          <h1 className="font-serif-display text-white text-5xl md:text-6xl leading-tight max-w-2xl mb-6">
            AKSES PEMBELIAN KONTEN ISLAMIC
          </h1>
          <p className="text-[#d9a94e] text-xl md:text-2xl font-medium mb-4">Assalamu’alaikum, Sobat Pencari Berkah!</p>
          <p className="text-white/75 max-w-2xl leading-relaxed mb-8">
            Alhamdulillah, terima kasih sudah memesan Paket Eksklusif 7.000+ Konten Islamic. Semoga konten ini menjadi jalan untuk menyebarkan kebaikan dan menjemput keberkahan.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#islamic" className="gold-gradient text-[#0a3b2c] font-semibold px-6 py-3 rounded-lg hover:brightness-105 transition">Lihat Konten</a>
            <a href="#bonus" className="border border-white/30 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/10 transition">Lihat Bonus</a>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16">
        {/* Perhatian + Cara Instal */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          <div className="bg-[#fdf6e3] border border-[#e6d9a8] rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-[#c9962f]" />
              <h3 className="font-serif-display text-2xl text-[#0a3b2c] tracking-wide">PERHATIAN — MOHON DIPERHATIKAN</h3>
            </div>
            {perhatianText.map((t, i) => (
              <p key={i} className="text-[#4a5a52] text-sm leading-relaxed italic mb-3">{t}</p>
            ))}
            <div className="flex items-start gap-2 bg-[#fbe9e9] border border-[#f0c4c4] rounded-xl p-4 mt-4">
              <ShieldAlert className="w-5 h-5 text-[#c0392b] shrink-0 mt-0.5" />
              <p className="text-[#c0392b] text-sm leading-relaxed">
                Mohon untuk tidak membagikan dan menyebarkan link aksesnya! Jika dibagikan maka akses akan otomatis terdeteksi sistem dan kamu tidak akan bisa akses lagi.
              </p>
            </div>
          </div>

          <div className="bg-white border border-[#e8e2d0] rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Info className="w-5 h-5 text-[#0a7a52]" />
              <h3 className="font-serif-display text-2xl text-[#0a3b2c] tracking-wide">CARA INSTAL</h3>
            </div>
            <ol className="space-y-4 list-none p-0 m-0">
              {caraInstal.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-[#0a3b2c] text-[#d9a94e] text-xs font-semibold flex items-center justify-center mt-0.5">{i + 1}</span>
                  <span className="flex-1 min-w-0 text-[#4a5a52] text-sm leading-relaxed break-words">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Footage */}
        <section id="footage" className="mb-16 scroll-mt-20">
          <CategoryHeader icon={Film} label="KATEGORI" title="Konten Footage" desc="Footage ini bisa kamu edit dan sesuaikan sendiri sesuai kebutuhan kontenmu. Videonya belum ada suara atau musik, jadi kamu bebas menambahkan sound sesuai selera." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {footageContent.map((f) => (
              <FileCard key={f.id} item={f} icon={Film} onOpen={openLink} />
            ))}
          </div>
        </section>

        {/* Islamic */}
        <section id="islamic" className="mb-16 scroll-mt-20">
          <CategoryHeader icon={BookOpen} label="KATEGORI" title="Konten Islamic" desc="Kumpulan 7.000+ konten Islamic siap upload untuk kontenmu." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {islamicContent.map((f) => (
              <FileCard key={f.id} item={f} icon={BookOpen} onOpen={openLink} />
            ))}
          </div>
        </section>

        {/* Bonus */}
        <section id="bonus" className="mb-16 scroll-mt-20 islamic-pattern rounded-3xl p-7 md:p-10">
          <div className="flex items-center gap-2 mb-1">
            <Gift className="w-4 h-4 text-[#d9a94e]" />
            <span className="text-[#d9a94e] text-xs font-semibold tracking-[0.25em]">SPESIAL</span>
          </div>
          <h2 className="font-serif-display text-white text-4xl mb-2">Akses Bonus</h2>
          <p className="text-white/60 text-sm mb-7">Bonus tambahan spesial untukmu sebagai member.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {bonusContent.map((b) => (
              <button key={b.id} onClick={() => openLink(b.url)} className="bonus-card flex items-center gap-3 text-left bg-[#0a3b2c] border border-[#d9a94e]/20 rounded-xl p-4 group">
                <span className="shrink-0 w-10 h-10 rounded-lg gold-gradient flex items-center justify-center">
                  <Gift className="w-5 h-5 text-[#0a3b2c]" />
                </span>
                <span className="flex-1 text-white text-sm font-semibold leading-snug">{b.title}</span>
                <ArrowUpRight className="w-4 h-4 text-white/50 group-hover:text-[#d9a94e] transition-colors" />
              </button>
            ))}
          </div>
        </section>

        {/* Kelas */}
        <section className="mb-16">
          <div className="flex items-center gap-2 mb-1">
            <GraduationCap className="w-5 h-5 text-[#c9962f]" />
            <span className="text-[#c9962f] text-xs font-semibold tracking-[0.25em]">UPGRADE SKILL</span>
          </div>
          <h2 className="font-serif-display text-4xl text-[#0a3b2c] mb-2">Ingin Dibimbing Mentor untuk Cuan &amp; Pahala?</h2>
          <p className="text-[#4a5a52] text-sm max-w-2xl mb-3">Dapatkan bimbingan langsung dari mentor untuk membantu kamu memahami cara menghasilkan cuan melalui Facebook Pro &amp; YouTube Shorts. Bukan hanya mengejar penghasilan, tapi juga membuka peluang berbagi manfaat dan mendapatkan pahala dari aktivitas yang bernilai kebaikan.</p>
          <p className="text-[#4a5a52] text-sm max-w-2xl mb-6">Belajar dengan arahan mentor, pahami langkahnya, dan mulai ikhtiar mendapatkan penghasilan halal sambil berbagi manfaat.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <button onClick={() => openLink('https://wa.me/6288994298767')} className="file-card flex items-center justify-between gap-3 text-left bg-white border border-[#e8e2d0] rounded-xl p-5">
              <span className="text-[#0a3b2c] font-semibold text-sm">WHATSAPP</span>
              <ArrowUpRight className="w-5 h-5 text-[#c9962f] shrink-0" />
            </button>
          </div>
        </section>

        {/* Support banner */}
        <div className="islamic-pattern rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-full gold-gradient flex items-center justify-center shrink-0">
              <Headphones className="w-5 h-5 text-[#0a3b2c]" />
            </span>
            <p className="text-white/85 text-sm">Jika ada kendala atau akses videonya error, silakan hubungi admin agar langsung dibantu.</p>
          </div>
          <span className="gold-gradient text-[#0a3b2c] text-sm font-semibold px-4 py-2 rounded-full whitespace-nowrap">Support 24 Jam</span>
        </div>
      </main>

      <footer className="islamic-pattern border-t border-[#d9a94e]/15 py-6">
        <p className="text-center text-white/50 text-sm">© 2026 Pahala Digital. Barakallahu fiik.</p>
      </footer>
    </div>
  );
}

function CategoryHeader({ icon: Icon, label, title, desc }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-[#c9962f]" />
        <span className="text-[#c9962f] text-xs font-semibold tracking-[0.25em]">{label}</span>
      </div>
      <h2 className="font-serif-display text-4xl text-[#0a3b2c] mb-2">{title}</h2>
      <p className="text-[#4a5a52] text-sm max-w-2xl leading-relaxed">{desc}</p>
    </div>
  );
}

function FileCard({ item, icon: Icon, onOpen }) {
  return (
    <div className="file-card flex items-center gap-3 bg-white border border-[#e8e2d0] rounded-xl p-4">
      <span className="shrink-0 w-11 h-11 rounded-lg bg-[#0a3b2c] flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#d9a94e]" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[#c9962f] text-[10px] font-semibold tracking-wider">{item.label}</p>
        <p className="text-[#0a3b2c] font-semibold text-sm truncate">{item.title}</p>
      </div>
      <button onClick={() => onOpen(item.url)} className="flex items-center gap-1.5 bg-[#f4eede] hover:bg-[#e9e0c8] text-[#0a3b2c] text-sm font-medium px-3.5 py-2 rounded-lg transition">
        <Download className="w-4 h-4" /> Buka
      </button>
    </div>
  );
}
